/**
 * Links session-memo persistence — survives page refresh within the same
 * investigation, invalidated when the investigation id or canvas version changes.
 *
 * Verdicts: 'no-path' | 'has-path' keyed by canonical `CHAIN:lo|hi` pair keys.
 */
export type LinksMemoVerdict = 'no-path' | 'has-path';

export const LINKS_MEMO_STORAGE_PREFIX = 'wb:links-memo';

export interface LinksMemoSnapshot {
    investigationId: string;
    version: string;
    attempts: Record<string, LinksMemoVerdict>;
}

export function linksMemoStorageKey(investigationId: string, version: string): string {
    const id = investigationId || 'draft';
    const ver = version || '0';
    return `${LINKS_MEMO_STORAGE_PREFIX}:${id}:v${ver}`;
}

/**
 * Stable canvas version from the set of address-node ids. Adding/removing a
 * wallet invalidates the memo so pair checks re-run against the new graph.
 */
export function computeLinksMemoVersion(addressNodeIds: Iterable<string>): string {
    const ids = Array.from(addressNodeIds)
        .map(String)
        .filter(Boolean)
        .sort();
    // FNV-1a 32-bit over the joined id list — short, stable, collision-resistant enough for a memo key.
    const joined = ids.join('|');
    let hash = 0x811c9dc5;
    for (let i = 0; i < joined.length; i++) {
        hash ^= joined.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return `${ids.length}-${(hash >>> 0).toString(16)}`;
}

export function loadLinksMemo(
    investigationId: string,
    version: string,
    storage: Pick<Storage, 'getItem'> | null = typeof localStorage !== 'undefined' ? localStorage : null,
): Map<string, LinksMemoVerdict> {
    const map = new Map<string, LinksMemoVerdict>();
    if (!storage) return map;
    try {
        const raw = storage.getItem(linksMemoStorageKey(investigationId, version));
        if (!raw) return map;
        const parsed = JSON.parse(raw) as Partial<LinksMemoSnapshot>;
        if (parsed.investigationId !== (investigationId || 'draft')) return map;
        if (parsed.version !== (version || '0')) return map;
        const attempts = parsed.attempts || {};
        for (const [k, v] of Object.entries(attempts)) {
            if (v === 'no-path' || v === 'has-path') map.set(k, v);
        }
    } catch {
        /* corrupt / quota — start fresh */
    }
    return map;
}

export function saveLinksMemo(
    investigationId: string,
    version: string,
    attempts: Map<string, LinksMemoVerdict> | Record<string, LinksMemoVerdict>,
    storage: Pick<Storage, 'setItem' | 'removeItem'> | null = typeof localStorage !== 'undefined' ? localStorage : null,
): void {
    if (!storage) return;
    const record: Record<string, LinksMemoVerdict> =
        attempts instanceof Map ? Object.fromEntries(attempts.entries()) : { ...attempts };
    const snapshot: LinksMemoSnapshot = {
        investigationId: investigationId || 'draft',
        version: version || '0',
        attempts: record,
    };
    try {
        storage.setItem(linksMemoStorageKey(investigationId, version), JSON.stringify(snapshot));
    } catch {
        /* quota / private mode — ignore */
    }
}

/** Drop every links-memo key for an investigation (all versions). */
export function clearLinksMemoForInvestigation(
    investigationId: string,
    storage: Pick<Storage, 'length' | 'key' | 'removeItem'> | null = typeof localStorage !== 'undefined' ? localStorage : null,
): void {
    if (!storage) return;
    const prefix = `${LINKS_MEMO_STORAGE_PREFIX}:${investigationId || 'draft'}:`;
    const toRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && key.startsWith(prefix)) toRemove.push(key);
    }
    for (const key of toRemove) {
        try {
            storage.removeItem(key);
        } catch {
            /* ignore */
        }
    }
}
